import React from 'react';
import { Stack, Box, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import playersIcon from '/images/lobby/mobile/players.svg';
import chipsIcon from '/images/lobby/mobile/chip.svg';
import styles from '../../../assets/css/components/MobileLobby.module.css';
import { IRoomState } from '../../../Core/State/IRoomState';
import { IUserState } from '../../../Core/State/IUserState';
import { t } from '../../../i18n';

import bnbIcon from '/crypto/bnb.svg';
import busdIcon from '/crypto/busd.svg';
import daiIcon from '/crypto/dai.svg';
import dogeIcon from '/crypto/doge.svg';
import dotIcon from '/crypto/dot.svg';
import shibIcon from '/crypto/shib.svg';
import solIcon from '/crypto/sol.svg';
import solamaIcon from '/crypto/solama.svg';
import usdcIcon from '/crypto/usdc.svg';
import usdtIcon from '/crypto/usdt.svg';
import vlxIcon from '/crypto/vlx.svg';
import xmrIcon from '/crypto/xmr.svg';

interface MobileTableRowProps {
    table: IRoomState;
    joinedRoomsState: any[];
    joinRoom: (roomId: string) => void;
    userState: IUserState;
}

const MobileTableRow: React.FC<MobileTableRowProps> = ({ table, joinedRoomsState, joinRoom, userState }) => {

    const getHighlightColor = (table: IRoomState): string => {
        if (isUserSeated(table)) {
            return '#EBC54F';
        }
        if (isUserObserving(table)) {
            return '#ffffff';
        }
        if (table.playerSatCount < table.minPlayers) {
            return '#ae982d';
        }
        if (table.playerSatCount === table.maxPlayers) {
            return '#f52740';
        }
        return '#42c079';
    }

    const getGameTypeColor = (table: IRoomState): string => {
        switch (table.type.toLowerCase()) {
            case "texas":
                return "#4ddaa2";
            case 'aof':
                return '#fc5468';
            default:
                return '#d76dfd';
        }
    }

    const getTableStatus = (table: IRoomState): React.ReactElement => {
        if (isUserSeated(table)) {
            return (
                <Typography style={{ color: '#EBC54F' }}>{t('common:table.youAreSeated')}</Typography>
            );
        }
        if (isUserObserving(table)) {
            return (
                <Typography style={{ color: '#ffffff' }}>{t('common:table.youAreObserving')}</Typography>
            );
        }
        let message: React.ReactElement | string = t('common:table.openSeats');
        if (table.playerSatCount < table.minPlayers) {
            let requiredPlayers = table.minPlayers - table.playerSatCount;
            message = t('common:table.needPlayersToStart', { count: requiredPlayers });
        } else if (table.playerSatCount === table.maxPlayers) {
            message = t('common:table.tableFull');
        }
        return (
            <Typography style={{ color: getHighlightColor(table) }}>{message}</Typography>
        );
    }

    const formatCurrency = (value: number, currencyType: string) => {
        const symbol = currencyType === 'crypto' ? '' : '$';
        let formatted: string;

        if (Math.abs(value) >= 1_000_000) {
            formatted = `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 2)}M`;
        } else if (Math.abs(value) >= 1_000) {
            formatted = `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 2)}K`;
        } else {
            formatted = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                maximumFractionDigits: 2
            }).format(value);
        }

        return `${symbol}${formatted}`;
    };

    const isUserObserving = (table: IRoomState): boolean => {
        return joinedRoomsState.some((room) => room.roomId === table.roomId);
    };

    const isUserSeated = (table: IRoomState): boolean => {
        return table.players.some((player) => player.userId === userState.id);
    };

    const getCryptoIcon = (currency: string) => {
        switch(currency?.toLowerCase()) {
            case 'bnb':
                return bnbIcon;
            case 'busd':
                return busdIcon;
            case 'dai':
                return daiIcon;
            case 'doge':
                return dogeIcon;
            case 'dot':
                return dotIcon;
            case 'shib':
                return shibIcon;
            case 'sol':
                return solIcon;
            case 'solama':
                return solamaIcon;
            case 'usdc':
                return usdcIcon;
            case 'usdt':
                return usdtIcon;
            case 'vlx':
                return vlxIcon;
            case 'xmr':
                return xmrIcon;
            default:
                return undefined;
        }
    };

    return (
        <Stack key={table.roomId} direction="row" spacing={0.5} className={styles.mobileTable} sx={{ '--game-type-color': getGameTypeColor(table), '--table-highlight-color' : getHighlightColor(table) }} onClick={() => joinRoom(table.roomId)}>
            <Box className={styles.mobileTablePlayers} style={table.currencyType === 'crypto' ? { padding: '5px 12px' } : { padding: '12px' }}>
                {table.currencyType === 'crypto' && (
                    <Stack alignItems="center">
                        <Box className={styles.currencyIcon}>
                            <img src={getCryptoIcon(table.currency)} alt={`${table.currency} Icon`} width={36} />
                        </Box>
                        <span className={styles.currencyText}>{table.currency}</span>
                    </Stack>
                )}
                {table.currencyType === 'fiat' && (
                    <Typography><img src={playersIcon} alt="Players Icon" style={{ marginRight: '4px' }} /> {Math.min(table.playerSatCount, table.maxPlayers)}/{table.maxPlayers}</Typography>
                )}
            </Box>
            <Stack className={styles.mobileTableInfo}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" className={styles.mobileTableTitle}>
                    <Typography className={styles.mobileTableName}>
                        {table.name}
                        {isUserSeated(table) ? (
                            <EventSeatIcon style={{ fontSize: '16px', marginLeft: '8px' }} />
                        ) : isUserObserving(table) ? (
                            <VisibilityIcon style={{ fontSize: '16px', marginLeft: '8px' }} />
                        ) : null}
                    </Typography>
                    <Typography className={styles.mobileTableType} style={{ marginLeft: 'auto' }}>
                        {(() => {
                            switch (table.type.toLowerCase()) {
                                case "texas":
                                    return t('common:gameTypes.holdem');
                                case 'aof':
                                    return t('common:gameTypes.aof');
                                default:
                                    return table.type.charAt(0).toUpperCase() + table.type.slice(1);
                            }
                        })()}
                    </Typography>
                    {table.currencyType === 'crypto' && (
                        <Typography className={styles.mobileTableCurrency} style={{ marginLeft: '4px' }}>
                            {t('common:labels.crypto')}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" className={styles.mobileTableTraits}>
                    {getTableStatus(table)}
                </Stack>
                <Stack direction="row" className={styles.mobileTableStakes}>
                    <Stack direction="row" alignContent="center" alignItems="center">
                        {table.ante && table.ante > 0 ? <Typography style={{ marginRight: '4px' }}>{t('common:labels.ante')}:</Typography> : <img src={chipsIcon} alt="Chip Icon" style={{ marginRight: '4px' }} />}
                        {table.currencyType === 'crypto' && (
                            <img src={getCryptoIcon(table.currency)} alt={`${table.currency} Icon`} style={{ marginRight: '4px' }} height={14} />
                        )}
                        <Typography>
                            {table.ante && table.ante > 0 ?
                                `${formatCurrency(table.ante, table.currencyType)}` :
                                `${formatCurrency(table.smallBlind, table.currencyType)}/${formatCurrency(table.bigBlind, table.currencyType)}`
                            }
                        </Typography>
                        <span style={{ marginLeft: "16px" }}></span>
                        <Typography style={{ marginRight: '4px' }}>{t('common:labels.buyIn')}:</Typography>
                        {table.currencyType === 'crypto' && (
                            <img src={getCryptoIcon(table.currency)} alt={`${table.currency} Icon`} style={{ marginRight: '4px' }} height={14} />
                        )}
                        <Typography>
                            {table.type.toLowerCase() === 'aof' ?
                                `${formatCurrency(table.maxBuyIn, table.currencyType)}` :
                                `${formatCurrency(table.minBuyIn, table.currencyType)} / ${formatCurrency(table.maxBuyIn, table.currencyType)}`
                            }
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default MobileTableRow;
